import React, { useRef, useState, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import useImage from 'use-image';
import { TextBlock } from './imageTranslatorTypes.types';
import EditableText from './EditableText';
import Konva from 'konva';
import { rgbToHex } from './imageTranslatorUtils';

// Add interface for the ref handle
export interface ImageCanvasHandle {
  downloadCanvas: () => Promise<string>;
  stageRef: React.RefObject<Konva.Stage>;
  getImageDimensions: () => { width: number; height: number } | null;
}

interface ImageCanvasProps {
  imageUrl: string;
  textBlocks: TextBlock[];
  onTextBlockChange: (blockId: string, updatedBlock: Partial<TextBlock>) => void;
  onTextBlockDelete: (id: string) => void;
  onSelectBlock: (blockId: string, ctrlKey: boolean) => void;
  isPickingColor: boolean;
  onPickColor: (color: string) => void;
  selectedBlockIds: Set<string>;
  isFinished: boolean;
}

const ImageCanvas = forwardRef<ImageCanvasHandle, ImageCanvasProps>(({
  imageUrl,
  textBlocks,
  onTextBlockChange,
  onTextBlockDelete,
  onSelectBlock,
  isPickingColor,
  onPickColor,
  selectedBlockIds,
  isFinished,
}, ref) => {
  const [image] = useImage(imageUrl);
  const stageRef = useRef<Konva.Stage | null>(null);
  const layerRef = useRef<Konva.Layer | null>(null);
  const imageRef = useRef<Konva.Image | null>(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const scale = useMemo(() => {
    if (!image) return 1;
    const aspectRatio = image.width / image.height;
    const width = Math.min(800, window.innerWidth - 40);
    const height = width / aspectRatio;
    setStageSize({ width, height });
    
    const scaleX = width / image.width;
    return scaleX;
  }, [image]);

  // Add imperative handle
  useImperativeHandle(ref, () => ({
    downloadCanvas: async () => {
      if (!stageRef.current) return '';

      // Hide transformers before downloading
      const transformers = stageRef.current.find('Transformer');
      transformers.forEach(transformer => transformer.hide());

      // Convert stage to data URL
      const dataURL = stageRef.current.toDataURL({ pixelRatio: 1 });

      // Show transformers again
      transformers.forEach(transformer => transformer.show());

      return dataURL;
    },
    stageRef: stageRef,
    layerRef: layerRef,
    getImageDimensions: () => {
      if (!image) return null;
      return { width: image.width, height: image.height };
    },
  }), [image]);

  // Function to get color from image at specific coordinates
  const getColorAtPoint = useCallback((x: number, y: number): string => {
    if (!imageRef.current || !stageRef.current) return '#000000';

    const image = imageRef.current;

    // Get relative position within the image
    const transform = image.getAbsoluteTransform().copy();
    const inversedTransform = transform.invert();
    const pos = inversedTransform.point({ x, y });

    // Create temporary canvas to get pixel data
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '#000000';

    canvas.width = image.width();
    canvas.height = image.height();
    ctx.drawImage(image.image()!, 0, 0, canvas.width, canvas.height);

    // Get pixel color
    const imageData = ctx.getImageData(pos.x, pos.y, 1, 1).data;
    const hex = rgbToHex(imageData[0], imageData[1], imageData[2]);
    
    return hex;
  }, []);

  // Handle stage click for color picking
  const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (isFinished) return;
    
    if (isPickingColor) {
      const stage = e.target.getStage();
      if (!stage) return;

      const point = stage.getPointerPosition();
      if (!point) return;

      const color = getColorAtPoint(point.x, point.y);
      onPickColor(color);
      return;
    }
    
    // Only deselect if clicking on stage or image and ctrl key not pressed
    if ((e.target === e.target.getStage() || e.target === imageRef.current)
      && !e.evt.ctrlKey) {
      onSelectBlock('', false);
    }
  }, [isFinished, isPickingColor, onPickColor, getColorAtPoint, onSelectBlock]);

  const handleTextBlockDelete = useCallback((id: string) => {
    if (isFinished) return;
    
    // Check if the deleted block was selected
    if (selectedBlockIds.has(id)) {
      // Find the index of the deleted block
      const blockIndex = textBlocks.findIndex(block => block.id === id);
      const hasMoreBlocks = textBlocks.length > 1

      // Delete the block
      onTextBlockDelete(id);
      
      // If there are other blocks, select the next one or the previous one if deleting the last block
      if (hasMoreBlocks) {
        const nextBlockId = (textBlocks[blockIndex + 1] ?? textBlocks[0]).id;
        // Use setTimeout to ensure this happens after the delete operation is processed
        setTimeout(() => {
          onSelectBlock(nextBlockId, false);
        }, 0);
      }
    } else {
      // If the deleted block wasn't selected, just delete it
      onTextBlockDelete(id);
    }
  }, [onTextBlockDelete, isFinished, textBlocks, selectedBlockIds, onSelectBlock]);

  React.useEffect(() => {
    
  }, [image]);

  // Add cursor style based on color picking mode
  const stageStyle = useMemo(() => ({
    cursor: isPickingColor ? 'crosshair' : undefined
  }), [isPickingColor]);

  // Add a message when there are no text blocks
  const NoTextMessage = () => (
    <div 
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        padding: '1rem',
        background: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        pointerEvents: 'none',
      }}
    >
      <p className="text-gray-600 text-sm">
        No text detected in this image
      </p>
    </div>
  );

  return (
    <div style={{ position: 'relative' }}>
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        className="border border-gray-300 rounded-lg"
        onClick={handleStageClick}
        style={stageStyle}
      >
        <Layer ref={layerRef}>
          {image && (
            <KonvaImage
              ref={imageRef}
              image={image}
              width={stageSize.width}
              height={stageSize.height}
            />
          )}
          {textBlocks.map((block) => (
            <EditableText
              key={block.id}
              block={block}
              scale={scale}
              isSelected={selectedBlockIds.has(block.id)}
              onChange={onTextBlockChange}
              onDeleteBlock={handleTextBlockDelete}
              onSelectBlock={onSelectBlock}
              isFinished={isFinished}
            />
          ))}
        </Layer>
      </Stage>
      {textBlocks.length === 0 && !isFinished && <NoTextMessage />}
    </div>
  );
});

export default React.memo(ImageCanvas);